if (!customElements.get('de-infinite-products')) {
  customElements.define('de-infinite-products', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.viewport = this.querySelector('.de-infinite__viewport');
      this.group = this.querySelector('[data-original]');
      this.button = this.querySelector('[data-pause]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.paused = this.motion.matches;
      this.interacting = false;
      this.pointerHeld = false;
      this.touchInput = false;
      this.hovered = false;
      this.focused = false;
      this.visible = false;
      this.position = this.viewport.scrollLeft;
      this.lastTime = 0;
      this.button.hidden = false;
      this.updateButton();
      this.button.addEventListener('click', () => { this.paused = !this.paused; this.updateButton(); }, options);
      this.querySelector('[data-mobile-nav]')?.removeAttribute('hidden');
      this.querySelector('[data-previous]')?.addEventListener('click', () => this.advance(-1), options);
      this.querySelector('[data-next]')?.addEventListener('click', () => this.advance(1), options);
      this.motion.addEventListener('change', () => { this.paused = this.motion.matches; this.updateButton(); }, options);
      this.viewport.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') this.hovered = true; }, options);
      this.viewport.addEventListener('pointerleave', () => { this.hovered = false; }, options);
      this.viewport.addEventListener('focusin', () => { this.focused = !this.touchInput; }, options);
      this.viewport.addEventListener('focusout', () => { this.focused = this.viewport.contains(document.activeElement); }, options);
      this.viewport.addEventListener('pointerdown', event => {
        this.touchInput = event.pointerType === 'touch';
        if (this.touchInput) this.focused = false;
        this.pointerHeld = true;
        this.holdMovement();
      }, { ...options, passive: true });
      const release = () => { this.pointerHeld = false; if (this.interacting) this.holdMovement(); };
      window.addEventListener('pointerup', release, options);
      window.addEventListener('pointercancel', release, options);
      this.viewport.addEventListener('keydown', () => {
        this.touchInput = false;
        this.focused = true;
        this.holdMovement();
      }, options);
      this.viewport.addEventListener('wheel', () => this.holdMovement(), { ...options, passive: true });
      this.viewport.addEventListener('scroll', () => {
        if (this.interacting) this.holdMovement();
      }, { ...options, passive: true });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; });
      this.observer.observe(this);
      this.resizeObserver = new ResizeObserver(() => { this.distance = this.group.getBoundingClientRect().width; });
      this.resizeObserver.observe(this.group);
      this.rebuildClone();
      const tick = time => {
        const elapsed = this.lastTime ? Math.min(time - this.lastTime, 64) / 1000 : 0;
        this.lastTime = time;
        if (!this.paused && !this.interacting && !this.hovered && !this.focused && this.visible && !document.hidden && this.distance > 0) {
          // Ease back into autoplay instead of accelerating abruptly after a swipe.
          this.currentSpeed = Math.min((this.currentSpeed || 0) + 30 * elapsed, Number(this.dataset.speed) * (this.mobile.matches ? 0.75 : 1));
          this.position = (this.position + this.currentSpeed * elapsed) % this.distance;
          this.viewport.scrollLeft = this.position;
        } else {
          this.position = this.viewport.scrollLeft;
          this.currentSpeed = 0;
        }
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
      this.loadRemaining();
    }
    updateButton() {
      this.button.textContent = this.paused ? 'Reanudar movimiento' : 'Pausar movimiento';
      this.button.setAttribute('aria-pressed', String(this.paused));
    }
    holdMovement() {
      this.interacting = true;
      clearTimeout(this.resumeTimer);
      if (this.pointerHeld) return;
      // Every inertial scroll event restarts this timer: never fight a native swipe.
      this.resumeTimer = setTimeout(() => {
        this.position = this.viewport.scrollLeft;
        this.interacting = false;
      }, 1800);
    }
    advance(direction) {
      const cards = [...this.group.querySelectorAll('[data-product-id]')];
      if (!cards.length || !this.distance) return;
      this.holdMovement();
      const current = this.viewport.scrollLeft % this.distance;
      const start = this.group.getBoundingClientRect().left;
      const positions = cards.map(card => card.getBoundingClientRect().left - start);
      let target = direction > 0
        ? positions.find(value => value > current + 2)
        : positions.findLast(value => value < current - 2);
      // Normalize a visible duplicate before moving through the original cards.
      if (this.viewport.scrollLeft >= this.distance) this.viewport.scrollLeft = current;
      if (target === undefined) {
        if (direction > 0) {
          target = this.distance;
        } else {
          this.viewport.scrollLeft = this.distance + current;
          target = positions[positions.length - 1];
        }
      }
      this.viewport.scrollTo({ left: target, behavior: this.motion.matches ? 'instant' : 'smooth' });
    }
    rebuildClone() {
      this.querySelector('[data-clone]')?.remove();
      if (!this.group.querySelector('[data-product-id]')) return;
      const clone = this.group.cloneNode(true);
      clone.removeAttribute('data-original');
      clone.setAttribute('data-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a').forEach(link => link.tabIndex = -1);
      clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      this.group.after(clone);
      this.distance = this.group.getBoundingClientRect().width;
    }
    async loadRemaining() {
      let next = this.group.querySelector('[data-next-page]')?.dataset.url;
      const visited = new Set();
      const ids = new Set([...this.group.querySelectorAll('[data-product-id]')].map(card => card.dataset.productId));
      try {
        while (next && !visited.has(next)) {
          visited.add(next);
          const url = new URL(next, location.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Catalog request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = [...doc.querySelectorAll('de-infinite-products')].find(element => element.dataset.sectionId === this.dataset.sectionId);
          if (!source) throw new Error('Section missing');
          for (const card of source.querySelectorAll('[data-original] [data-product-id]')) {
            if (!ids.has(card.dataset.productId)) {
              ids.add(card.dataset.productId);
              this.group.append(document.importNode(card, true));
            }
          }
          next = source.querySelector('[data-next-page]')?.dataset.url;
          this.rebuildClone();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        const status = this.querySelector('.de-infinite__status');
        status.textContent = 'No pudimos cargar el resto del catálogo. Puedes consultarlo en «Ver todos».';
        status.hidden = false;
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.resumeTimer);
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }
  });
}
